/**
 * What each key, mouse button and controller button does, and the player's changes to it.
 *
 * Everything that reads a control asks this module by action ("reload", "jump"), never by key,
 * so a rebind reaches the movement, the prompts and the hints at once. The keyboard and the
 * controller keep separate tables, each with Halo's layout as its default, saved in this browser.
 *
 * A keyboard binding is a `KeyboardEvent.code` (the physical key, so a layout change does not
 * move it), `Mouse0`..`Mouse4`, or `WheelUp` / `WheelDown`. A controller binding is a standard
 * Gamepad API button index, optionally as a hold: RB tapped reloads, RB held picks up.
 */

export type Action =
  | 'forward' | 'back' | 'left' | 'right' | 'jump' | 'crouch'
  | 'fire' | 'zoom' | 'reload' | 'swap' | 'swapBack' | 'melee' | 'grenade' | 'grenadeSwitch'
  | 'equipment' | 'interact' | 'pickupLeft' | 'view' | 'scoreboard' | 'mute'

export type PadAction =
  | 'jump' | 'crouch' | 'fire' | 'grenade' | 'melee' | 'reload' | 'interact' | 'swap'
  | 'grenadeSwitch' | 'pickupLeft' | 'boost' | 'view' | 'zoom' | 'equipment' | 'scoreboard'

export interface PadBinding {
  button: number
  /** Acts once the button has been down for a moment, so the same button can also be tapped for something else. */
  hold?: boolean
}

interface ActionSpec<T> {
  id: T
  label: string
  group: string
  /** Shown under the label, for actions that change with what the player is doing. */
  note?: string
}

export const KEY_ACTIONS: readonly ActionSpec<Action>[] = [
  { id: 'forward', label: 'Move forward', group: 'Movement' },
  { id: 'back', label: 'Move back', group: 'Movement' },
  { id: 'left', label: 'Strafe left', group: 'Movement' },
  { id: 'right', label: 'Strafe right', group: 'Movement' },
  { id: 'jump', label: 'Jump', group: 'Movement', note: 'Horn, or boost in a Ghost or Chopper' },
  { id: 'crouch', label: 'Crouch', group: 'Movement', note: 'Brake; Banshee boost' },
  { id: 'fire', label: 'Fire', group: 'Combat', note: 'Left gun when dual wielding' },
  { id: 'zoom', label: 'Zoom', group: 'Combat', note: 'Right gun when dual wielding; Banshee fuel rod' },
  { id: 'reload', label: 'Reload', group: 'Combat' },
  { id: 'melee', label: 'Melee', group: 'Combat' },
  { id: 'grenade', label: 'Throw grenade', group: 'Combat' },
  { id: 'grenadeSwitch', label: 'Grenade type', group: 'Combat' },
  { id: 'swapBack', label: 'Switch weapon', group: 'Combat' },
  { id: 'swap', label: 'Next weapon', group: 'Combat' },
  { id: 'interact', label: 'Use', group: 'Actions', note: 'Pick up, enter or exit a vehicle, drop the flag' },
  { id: 'pickupLeft', label: 'Pick up in left hand', group: 'Actions' },
  { id: 'equipment', label: 'Deploy equipment', group: 'Actions' },
  { id: 'view', label: 'Camera / change seat', group: 'Actions' },
  { id: 'scoreboard', label: 'Scoreboard', group: 'Other' },
  { id: 'mute', label: 'Mute sound', group: 'Other' },
]

/** Kinds decide how a controller button behaves: held ones follow the button, pressed ones act once. */
export const PAD_ACTIONS: readonly (ActionSpec<PadAction> & { held?: boolean; scope?: 'foot' | 'vehicle' })[] = [
  { id: 'jump', label: 'Jump', group: 'Movement', held: true, note: 'Horn in a Warthog' },
  { id: 'crouch', label: 'Crouch', group: 'Movement', held: true, note: 'Brake in a vehicle' },
  { id: 'fire', label: 'Fire', group: 'Combat', held: true, note: 'Right gun when dual wielding' },
  { id: 'grenade', label: 'Throw grenade', group: 'Combat', note: 'Left gun when dual wielding; vehicle secondary' },
  { id: 'melee', label: 'Melee', group: 'Combat' },
  { id: 'reload', label: 'Reload', group: 'Combat' },
  { id: 'swap', label: 'Switch weapon', group: 'Combat' },
  { id: 'zoom', label: 'Zoom', group: 'Combat' },
  { id: 'grenadeSwitch', label: 'Grenade type', group: 'Combat', scope: 'foot' },
  { id: 'interact', label: 'Use', group: 'Actions', note: 'Pick up, enter or exit a vehicle, drop the flag' },
  { id: 'pickupLeft', label: 'Pick up in left hand', group: 'Actions', scope: 'foot' },
  { id: 'equipment', label: 'Deploy equipment', group: 'Actions' },
  { id: 'view', label: 'Camera / change seat', group: 'Actions' },
  { id: 'boost', label: 'Vehicle boost', group: 'Actions', held: true, scope: 'vehicle' },
  { id: 'scoreboard', label: 'Scoreboard', group: 'Other', held: true },
]

/** Standard Gamepad API button order, named as an Xbox pad labels them. */
export const PAD_BUTTON_NAMES = ['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'Back', 'Start', 'L3', 'R3', 'D-pad ↑', 'D-pad ↓', 'D-pad ◀', 'D-pad ▶'] as const
export const PAD_START = 9
/** Escape always pauses (the browser lets go of the mouse on it regardless), so it cannot be bound. */
export const RESERVED_KEYS = new Set(['Escape'])

/** Two slots a row is what the settings screen shows, so no default needs more. */
export const SLOTS = 2

const DEFAULT_KEYS: Record<Action, string[]> = {
  forward: ['KeyW', 'ArrowUp'], back: ['KeyS', 'ArrowDown'], left: ['KeyA', 'ArrowLeft'], right: ['KeyD', 'ArrowRight'],
  jump: ['Space'], crouch: ['ShiftLeft', 'KeyC'],
  fire: ['Mouse0'], zoom: ['Mouse2', 'KeyZ'], reload: ['KeyR'], melee: ['KeyF', 'KeyV'], grenade: ['KeyG'], grenadeSwitch: ['KeyB'],
  swapBack: ['Digit1'], swap: ['Digit2'],
  interact: ['KeyE'], pickupLeft: ['KeyQ'], equipment: ['KeyX'], view: ['KeyT'],
  scoreboard: ['Tab'], mute: ['KeyM'],
}

const A = 0, B = 1, X = 2, Y = 3, LB = 4, RB = 5, LT = 6, RT = 7, BACK = 8, L3 = 10, R3 = 11, DOWN = 13, LEFT = 14
const DEFAULT_PAD: Record<PadAction, PadBinding[]> = {
  jump: [{ button: A }], crouch: [{ button: L3 }], fire: [{ button: RT }], grenade: [{ button: LT }],
  melee: [{ button: B }], reload: [{ button: RB }], interact: [{ button: RB, hold: true }], swap: [{ button: Y }], zoom: [{ button: R3 }],
  grenadeSwitch: [{ button: LB }, { button: LEFT }], pickupLeft: [{ button: LB, hold: true }], boost: [{ button: LB }],
  view: [{ button: X }], equipment: [{ button: DOWN }], scoreboard: [{ button: BACK }],
}

const STORAGE_KEY = 'halo-ce.bindings'

interface Saved { keys?: Partial<Record<Action, string[]>>; pad?: Partial<Record<PadAction, PadBinding[]>> }

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

let keys = clone(DEFAULT_KEYS)
let pad = clone(DEFAULT_PAD)
let byCode = new Map<string, Action[]>()
const listeners = new Set<() => void>()

function load(): void {
  let saved: Saved = {}
  try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Saved } catch { /* Defaults. */ }
  // Saved rows replace their defaults one by one, so an action added in a later version starts on its default.
  for (const { id } of KEY_ACTIONS) {
    const row = saved.keys?.[id]
    if (Array.isArray(row)) keys[id] = row.filter(code => typeof code === 'string' && !RESERVED_KEYS.has(code)).slice(0, SLOTS)
  }
  for (const { id } of PAD_ACTIONS) {
    const row = saved.pad?.[id]
    if (Array.isArray(row)) pad[id] = row.filter(b => Number.isInteger(b?.button) && b.button >= 0 && b.button < PAD_BUTTON_NAMES.length && b.button !== PAD_START)
      .map(b => (b.hold ? { button: b.button, hold: true } : { button: b.button })).slice(0, SLOTS)
  }
  index()
}

function index(): void {
  byCode = new Map()
  for (const { id } of KEY_ACTIONS) for (const code of keys[id]) byCode.set(code, [...(byCode.get(code) ?? []), id])
}

function save(): void {
  const changedKeys: Saved['keys'] = {}, changedPad: Saved['pad'] = {}
  for (const { id } of KEY_ACTIONS) if (JSON.stringify(keys[id]) !== JSON.stringify(DEFAULT_KEYS[id])) changedKeys[id] = keys[id]
  for (const { id } of PAD_ACTIONS) if (JSON.stringify(pad[id]) !== JSON.stringify(DEFAULT_PAD[id])) changedPad[id] = pad[id]
  try {
    if (!Object.keys(changedKeys).length && !Object.keys(changedPad).length) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify({ keys: changedKeys, pad: changedPad }))
  } catch { /* Private windows: the change lasts until the page closes. */ }
  index()
  for (const listener of listeners) listener()
}

load()

export function keyBindings(action: Action): readonly string[] { return keys[action] }
export function padBindings(action: PadAction): readonly PadBinding[] { return pad[action] }

/** The actions a key or mouse button is bound to; usually one, none if it is free. */
export function keyActions(code: string): readonly Action[] { return byCode.get(code) ?? [] }

export function onBindingsChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Put `code` in one slot of an action, or clear the slot with null. A key does one thing: if
 * another action had it, it is taken from there, and that action is returned so the screen can say so.
 */
export function setKeyBinding(action: Action, slot: number, code: string | null): Action | undefined {
  let displaced: Action | undefined
  const row = keys[action]
  if (code) {
    const own = row.indexOf(code)
    // Already on this action: moving it to the other slot swaps the two.
    if (own >= 0) { if (slot < row.length) [row[own], row[slot]] = [row[slot], row[own]] }
    else {
      for (const { id } of KEY_ACTIONS) {
        const at = id === action ? -1 : keys[id].indexOf(code)
        if (at >= 0) { keys[id].splice(at, 1); displaced = id }
      }
      if (slot < row.length) row[slot] = code
      else row.push(code)
    }
  } else if (slot < row.length) row.splice(slot, 1)
  save()
  return displaced
}

const sameBinding = (a: PadBinding, b: PadBinding) => a.button === b.button && !!a.hold === !!b.hold
const scopeOf = (action: PadAction) => PAD_ACTIONS.find(a => a.id === action)?.scope
/** Boost only exists in a vehicle and grenade type only on foot, so the two can share LB. */
const overlaps = (a: PadAction, b: PadAction) => { const x = scopeOf(a), y = scopeOf(b); return !x || !y || x === y }

/** As `setKeyBinding`, for the controller: a tap and a hold of one button are different bindings. */
export function setPadBinding(action: PadAction, slot: number, binding: PadBinding | null): PadAction | undefined {
  let displaced: PadAction | undefined
  const held = PAD_ACTIONS.find(a => a.id === action)?.held
  // A held action (jump, fire) follows the button, so a hold of it means nothing more than a press.
  const next = binding && (binding.hold && !held ? { button: binding.button, hold: true } : { button: binding.button })
  const row = pad[action]
  if (next) {
    const own = row.findIndex(b => sameBinding(b, next))
    if (own >= 0) { if (slot < row.length) [row[own], row[slot]] = [row[slot], row[own]] }
    else {
      for (const { id } of PAD_ACTIONS) {
        if (id === action || !overlaps(id, action)) continue
        const at = pad[id].findIndex(b => sameBinding(b, next))
        if (at >= 0) { pad[id].splice(at, 1); displaced = id }
      }
      if (slot < row.length) row[slot] = next
      else row.push(next)
    }
  } else if (slot < row.length) row.splice(slot, 1)
  save()
  return displaced
}

export function resetKeyBindings(): void { keys = clone(DEFAULT_KEYS); save() }
export function resetPadBindings(): void { pad = clone(DEFAULT_PAD); save() }
export function keyBindingsAreDefault(): boolean { return JSON.stringify(keys) === JSON.stringify(DEFAULT_KEYS) }
export function padBindingsAreDefault(): boolean { return JSON.stringify(pad) === JSON.stringify(DEFAULT_PAD) }

// The keyboard's own letters, where the browser will say (Chrome, Edge): KeyQ is A on a French keyboard.
let layout: Map<string, string> | null = null
type LayoutNavigator = Navigator & { keyboard?: { getLayoutMap?: () => Promise<Map<string, string>> } }
;(navigator as LayoutNavigator).keyboard?.getLayoutMap?.().then(map => { layout = map; for (const listener of listeners) listener() }).catch(() => { /* QWERTY names. */ })

const NAMED: Record<string, string> = {
  Space: 'Space', Tab: 'Tab', Enter: 'Enter', Backspace: 'Backspace', CapsLock: 'Caps Lock',
  ShiftLeft: 'Shift', ShiftRight: 'Right Shift', ControlLeft: 'Ctrl', ControlRight: 'Right Ctrl',
  AltLeft: 'Alt', AltRight: 'Right Alt', MetaLeft: '⌘', MetaRight: 'Right ⌘',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  Mouse0: 'Left click', Mouse1: 'Middle click', Mouse2: 'Right click', Mouse3: 'Mouse 4', Mouse4: 'Mouse 5',
  WheelUp: 'Wheel up', WheelDown: 'Wheel down',
  Backquote: '`', Minus: '-', Equal: '=', BracketLeft: '[', BracketRight: ']', Backslash: '\\',
  Semicolon: ';', Quote: "'", Comma: ',', Period: '.', Slash: '/', Insert: 'Ins', Delete: 'Del', PageUp: 'PgUp', PageDown: 'PgDn',
}

/** A binding as a player reads it: `KeyE` is "E", `Mouse2` is "Right click". */
export function codeName(code: string): string {
  const local = layout?.get(code)
  if (local && local.trim()) return local.toUpperCase()
  if (NAMED[code]) return NAMED[code]
  if (code.startsWith('Key')) return code.slice(3)
  if (code.startsWith('Digit')) return code.slice(5)
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`
  return code
}

export function padBindingName(binding: PadBinding): string {
  const name = PAD_BUTTON_NAMES[binding.button] ?? `Button ${binding.button}`
  return binding.hold ? `hold ${name}` : name
}

/** The first key on an action, for prompts ("E pick up"); empty when nothing is bound. */
export function keyName(action: Action): string {
  const code = keys[action][0]
  return code ? codeName(code) : ''
}

/** Every key on an action, for the pause screen's list ("F/V melee"). */
export function keyNames(action: Action): string {
  return keys[action].map(codeName).join(' / ')
}

/** The first button on a controller action, hold included ("hold RB"); empty when nothing is bound. */
export function padName(action: PadAction): string {
  const binding = pad[action][0]
  return binding ? padBindingName(binding) : ''
}
